import {notEmpty} from '@wandb/weave/common/util/obj';
import * as _ from 'lodash';
import {omit} from 'lodash';

import {
  getMigratedSettings,
  SUPPORTED_WORKSPACE_SPEC_VERSION,
} from '../components/WorkspaceDrawer/Settings/migrateSettings';
import {
  type MultiRunWorkspaceViewType,
  type WholeFromType,
  type WorkspaceViewType,
} from '../state/views/types';
import {EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT} from './panelbankConfigs';
import * as Panels from './panels';
import {
  migratePanelBankSettingsToPanelSettings,
  migrateWorkspaceToPanelBank,
  panelBankSectionFromJSON,
  runSetConfigFromJSON,
} from './parseSpecUtils';
import type * as Section from './section';

const DEFAULT_SECTION_NAME = '';

export function parsedWorkspaceSpec(
  spec: any,
  viewType: WorkspaceViewType
): WholeFromType<WorkspaceViewType> {
  if (viewType === 'run-view') {
    return parsedRunWorkspaceSpec(spec);
  } else {
    return parsedMultiRunWorkspaceSpec(spec, viewType);
  }
}

/**
 * This function will parse and return a well formed object for
 * view types 'group-view' | 'project-view' | 'sweep-view'.
 */
export function parsedMultiRunWorkspaceSpec(
  spec: any,
  viewType: MultiRunWorkspaceViewType
) {
  return Object.assign({}, spec, {
    section: parsedMultiRunWorkspaceSpecSection(spec.section, viewType),
  });
}

function parsedMultiRunWorkspaceSpecSection(
  json: any,
  viewType: 'group-view' | 'project-view' | 'sweep-view'
): Section.Config | null {
  // WARNING!!: If you change how this works it will reset some user-defined run colors.
  // See comment in runSetConfigFromJSON.

  const sectionName = json.name || DEFAULT_SECTION_NAME;
  const runSets = _.isArray(json.runSets)
    ? json.runSets
        .map((rs: any, i: number) =>
          runSetConfigFromJSON(DEFAULT_SECTION_NAME, rs, i)
        )
        .filter(notEmpty)
    : undefined;
  const runSetCount = runSets != null ? runSets.length : 0;
  const panels =
    json.panels != null ? Panels.configFromJSON(json.panels) : null; // old spec (for 'Custom Visualizations' + pinnable panels)
  const panelBankConfig = migrateWorkspaceToPanelBank(
    viewType,
    json.panelBankConfig,
    panels
  );
  const panelBankSectionConfig = panelBankSectionFromJSON(
    json.panelBankSectionConfig || EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT,
    viewType
  );

  const {panelBankSettings, panelSettings} =
    migratePanelBankSettingsToPanelSettings(
      panelBankConfig.settings,
      json.settings
    );
  panelBankConfig.settings = panelBankSettings;

  const currWorkspaceSettings = omit(json?.workspaceSettings, 'ref');
  const workspaceSettings =
    Object.keys(currWorkspaceSettings).length === 0
      ? getMigratedSettings({
          ...panelBankSettings,
          ...json.settings,
        })
      : currWorkspaceSettings;

  const specVersion = json?.version ?? SUPPORTED_WORKSPACE_SPEC_VERSION;

  return {
    name: sectionName,
    version: specVersion,
    // configFromJSON is Panels|null, this coerces nulls to undefined
    panels: panels != null ? panels : undefined,
    workspaceSettings,
    panelBankConfig, // current spec (for PanelBank)
    panelBankSectionConfig,
    runSets,
    openRunSet:
      json.openRunSet != null && json.openRunSet < runSetCount
        ? json.openRunSet
        : undefined,
    openViz: json.openViz == null ? true : json.openViz,
    settings: panelSettings,
    customRunColors: json.customRunColors || {},
    customRunNames: json.customRunNames || {},
  };
}

function parsedRunWorkspaceSpec(spec: any) {
  const panels = Panels.configFromJSON(spec.panels);
  const panelBankConfig = migrateWorkspaceToPanelBank(
    'run-view',
    spec.panelBankConfig,
    panels
  );

  const {panelBankSettings, panelSettings} =
    migratePanelBankSettingsToPanelSettings(
      panelBankConfig.settings,
      spec.settings
    );
  panelBankConfig.settings = panelBankSettings;

  // Note - we're intentionally ignoring migrating settings for run workspaces
  // because they will eventually be deprecated

  return {
    ...spec,
    panels,
    panelBankConfig,
    settings: panelSettings,
  };
}
